import { useState, useEffect } from 'react';
import { X, FloppyDisk, Spinner } from '@phosphor-icons/react';
import { API } from './constants';

export const ClienteFormModal = ({ cliente, onClose, onSuccess }) => {
  const isEdit = !!cliente;
  const [form, setForm] = useState({
    nome: '',
    telefone: '',
    email: '',
    instagram: '',
    aniversario: '',
    observacoes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (cliente) {
      setForm({
        nome: cliente.nome || '',
        telefone: cliente.telefone || '',
        email: cliente.email || '',
        instagram: cliente.instagram || '',
        aniversario: cliente.aniversario || '',
        observacoes: cliente.observacoes || '',
      });
    }
  }, [cliente]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneMask = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    let masked = digits;
    if (digits.length > 2) {
      masked = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }
    if (digits.length > 7) {
      masked = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }
    return masked;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) {
      setError('Nome e obrigatorio');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const url = isEdit ? `${API}/clientes/${cliente.id}` : `${API}/clientes`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          telefone: form.telefone.replace(/\D/g, ''),
        }),
      });
      if (!res.ok) throw new Error('Erro ao salvar');
      onSuccess();
    } catch (err) {
      setError('Erro ao salvar cliente. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="cliente-form-modal"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in-up" style={{ animationDuration: '0.25s' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7]">
          <h2 className="text-xl font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
            {isEdit ? 'Editar Cliente' : 'Nova Cliente'}
          </h2>
          <button
            data-testid="btn-close-form"
            onClick={onClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5] transition-colors"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Nome */}
          <div>
            <label className="block text-xs font-medium text-[#71717A] uppercase tracking-wide mb-1.5">
              Nome *
            </label>
            <input
              data-testid="input-nome"
              type="text"
              value={form.nome}
              onChange={(e) => handleChange('nome', e.target.value)}
              placeholder="Nome da cliente"
              className="w-full px-4 py-3 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20 focus:border-[#1B4332]/40 transition-all"
              autoFocus
            />
          </div>

          {/* Telefone */}
          <div>
            <label className="block text-xs font-medium text-[#71717A] uppercase tracking-wide mb-1.5">
              Telefone
            </label>
            <input
              data-testid="input-telefone"
              type="text"
              value={form.telefone}
              onChange={(e) => handleChange('telefone', handlePhoneMask(e.target.value))}
              placeholder="(11) 99999-9999"
              className="w-full px-4 py-3 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20 focus:border-[#1B4332]/40 transition-all"
            />
          </div>

          {/* Email + Instagram */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#71717A] uppercase tracking-wide mb-1.5">
                Email
              </label>
              <input
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="email@exemplo.com"
                className="w-full px-4 py-3 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20 focus:border-[#1B4332]/40 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#71717A] uppercase tracking-wide mb-1.5">
                Instagram
              </label>
              <input
                data-testid="input-instagram"
                type="text"
                value={form.instagram}
                onChange={(e) => handleChange('instagram', e.target.value)}
                placeholder="@perfil"
                className="w-full px-4 py-3 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20 focus:border-[#1B4332]/40 transition-all"
              />
            </div>
          </div>

          {/* Aniversario */}
          <div>
            <label className="block text-xs font-medium text-[#71717A] uppercase tracking-wide mb-1.5">
              Aniversario
            </label>
            <input
              data-testid="input-aniversario"
              type="date"
              value={form.aniversario}
              onChange={(e) => handleChange('aniversario', e.target.value)}
              className="w-full px-4 py-3 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20 focus:border-[#1B4332]/40 transition-all"
            />
          </div>

          {/* Observacoes */}
          <div>
            <label className="block text-xs font-medium text-[#71717A] uppercase tracking-wide mb-1.5">
              Observacoes
            </label>
            <textarea
              data-testid="input-observacoes"
              value={form.observacoes}
              onChange={(e) => handleChange('observacoes', e.target.value)}
              placeholder="Preferencias, alergias, notas importantes..."
              rows={3}
              className="w-full px-4 py-3 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20 focus:border-[#1B4332]/40 transition-all resize-none"
            />
          </div>

          {/* Error */}
          {error && (
            <p data-testid="form-error" className="text-sm text-red-600">{error}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A] hover:bg-[#F4F4F5] transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="btn-salvar-cliente"
              className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#1B4332' }}
            >
              {saving ? (
                <Spinner size={18} className="animate-spin" />
              ) : (
                <FloppyDisk size={18} weight="bold" />
              )}
              {saving ? 'Salvando...' : isEdit ? 'Salvar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClienteFormModal;
